import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  CircularProgress,
  Typography
} from "@material-ui/core";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import useLeads from "../../hooks/useLeads";
import { format, parseISO } from "date-fns";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
}));

const LeadModal = ({ open, onClose, lead }) => {
  const classes = useStyles();
  const { save, update } = useLeads();

  const initialState = {
    name: "",
    email: "",
    gender: "",
    dateOfBirth: "",
    language: "",
    commercialEmail: "",
    country: "",
    city: "",
    state: "",
    source: "",
    entryDate: "",
    exitDate: "",
    dealValue: "",
    leadCompany: "",
    role: "",
    productOfInterest: "",
    observations: "",
    contactId: "",
  };

  const [leadData, setLeadData] = useState(initialState);
  const [loading, setLoading] = useState(false);
  
  // WhatsApp Contact Linking states
  const [options, setOptions] = useState([]);
  const [contactSearchParam, setContactSearchParam] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [loadingContacts, setLoadingContacts] = useState(false);

  useEffect(() => {
    if (lead) {
      setLeadData({
        ...lead,
        dateOfBirth: lead.dateOfBirth ? lead.dateOfBirth.split("T")[0] : "",
        entryDate: lead.entryDate ? lead.entryDate.split("T")[0] : "",
        exitDate: lead.exitDate ? lead.exitDate.split("T")[0] : "",
      });
      if (lead.contact) {
        setSelectedContact(lead.contact);
      } else {
        setSelectedContact(null);
      }
    } else {
      setLeadData(initialState);
      setSelectedContact(null);
    }
  }, [lead, open]);

  useEffect(() => {
    if (!open || contactSearchParam.length < 3) {
      setLoadingContacts(false);
      return;
    }
    setLoadingContacts(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("contacts", {
            params: { searchParam: contactSearchParam },
          });
          setOptions(data.contacts);
          setLoadingContacts(false);
        } catch (err) {
          setLoadingContacts(false);
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [contactSearchParam, open]);

  const handleClose = () => {
    onClose();
    setLeadData(initialState);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLeadData({ ...leadData, [name]: value });
  };

  const handleSaveLead = async () => {
    setLoading(true);
    try {
      const payload = { ...leadData };
      if (selectedContact) {
        payload.contactId = selectedContact.id;
      } else {
        payload.contactId = null;
      }

      // Convert empty strings to null for Date and Decimal fields to prevent DB cast errors
      if (payload.dateOfBirth === "") payload.dateOfBirth = null;
      if (payload.entryDate === "") payload.entryDate = null;
      if (payload.exitDate === "") payload.exitDate = null;
      if (payload.dealValue === "") payload.dealValue = null;

      if (lead && lead.id) {
        await update(payload);
      } else {
        await save(payload);
      }
      handleClose();
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const renderContactAutocomplete = () => {
    return (
      <Autocomplete
        fullWidth
        options={options}
        loading={loadingContacts}
        clearOnBlur
        autoHighlight
        freeSolo
        clearOnEscape
        value={selectedContact}
        getOptionLabel={(option) => {
          if (option && option.number) {
            return `${option.name} - ${option.number}`;
          }
          return `${option?.name || ""}`;
        }}
        renderOption={(option) => {
          if (option.number) {
            return (
              <Typography component="span" style={{ fontSize: 14 }}>
                {option.name} - {option.number}
              </Typography>
            );
          }
          return `${option.name}`;
        }}
        onChange={(e, newValue) => {
          setSelectedContact(newValue);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={"Vincular Contato do WhatsApp"}
            variant="outlined"
            onChange={(e) => setContactSearchParam(e.target.value)}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loadingContacts ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
      />
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle id="form-dialog-title">
        {lead ? "Editar Lead" : "Adicionar Lead"}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          {/* Sessão: Informações Pessoais */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" color="primary">Informações Pessoais</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Nome"
              name="name"
              value={leadData.name}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="E-mail"
              name="email"
              value={leadData.email}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel>Sexo</InputLabel>
              <Select
                name="gender"
                value={leadData.gender}
                onChange={handleChange}
                label="Sexo"
              >
                <MenuItem value=""><em>Nenhum</em></MenuItem>
                <MenuItem value="Masculino">Masculino</MenuItem>
                <MenuItem value="Feminino">Feminino</MenuItem>
                <MenuItem value="Outro">Outro</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Data de Nascimento"
              name="dateOfBirth"
              type="date"
              value={leadData.dateOfBirth}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Idioma"
              name="language"
              value={leadData.language}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
          </Grid>

          {/* Sessão: Profissional e Localização */}
          <Grid item xs={12} style={{ marginTop: '16px' }}>
            <Typography variant="subtitle1" color="primary">Profissional e Localização</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="E-mail Comercial"
              name="commercialEmail"
              value={leadData.commercialEmail}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Empresa"
              name="leadCompany"
              value={leadData.leadCompany}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Cargo"
              name="role"
              value={leadData.role}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="País"
              name="country"
              value={leadData.country}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Estado"
              name="state"
              value={leadData.state}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Cidade"
              name="city"
              value={leadData.city}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
          </Grid>

          {/* Sessão: Captação e Integração */}
          <Grid item xs={12} style={{ marginTop: '16px' }}>
            <Typography variant="subtitle1" color="primary">Captação e Venda</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel>Origem do Lead</InputLabel>
              <Select
                name="source"
                value={leadData.source}
                onChange={handleChange}
                label="Origem do Lead"
              >
                <MenuItem value=""><em>Nenhum</em></MenuItem>
                <MenuItem value="WhatsApp">WhatsApp</MenuItem>
                <MenuItem value="YouTube">YouTube</MenuItem>
                <MenuItem value="Facebook">Facebook</MenuItem>
                <MenuItem value="Instagram">Instagram</MenuItem>
                <MenuItem value="LinkedIn">LinkedIn</MenuItem>
                <MenuItem value="TikTok">TikTok</MenuItem>
                <MenuItem value="Google">Google</MenuItem>
                <MenuItem value="Site">Site</MenuItem>
                <MenuItem value="Formulario">Formulário</MenuItem>
                <MenuItem value="CRM">CRM</MenuItem>
                <MenuItem value="Outros">Outros</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Data de Entrada"
              name="entryDate"
              type="date"
              value={leadData.entryDate}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Valor do Negócio"
              name="dealValue"
              type="number"
              value={leadData.dealValue}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              InputProps={{ startAdornment: "R$" }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Produto de Interesse"
              name="productOfInterest"
              value={leadData.productOfInterest}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Data de Saída"
              name="exitDate"
              type="date"
              value={leadData.exitDate}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Observações"
              name="observations"
              value={leadData.observations}
              onChange={handleChange}
              fullWidth
              multiline
              rows={4}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} style={{ marginTop: '16px' }}>
            <Typography variant="subtitle1" color="primary" style={{ marginBottom: '8px' }}>
              Vínculo com Contato do Sistema
            </Typography>
            {renderContactAutocomplete()}
          </Grid>

        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          color="secondary"
          disabled={loading}
          variant="outlined"
        >
          {i18n.t("contactModal.buttons.cancel")}
        </Button>
        <div className={classes.btnWrapper}>
          <ButtonWithSpinner
            variant="contained"
            color="primary"
            disabled={loading || leadData.name === ""}
            onClick={handleSaveLead}
            loading={loading}
          >
            {lead ? i18n.t("contactModal.buttons.okEdit") : i18n.t("contactModal.buttons.okAdd")}
          </ButtonWithSpinner>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default LeadModal;
